import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "@/lib/utils";

interface CopyableTerminalProps {
  title?: string;
  children: ReactNode;
  copyText?: string;
  headerAction?: ReactNode;
  className?: string;
}

export function CopyableTerminal({
  title,
  children,
  copyText,
  headerAction,
  className,
}: CopyableTerminalProps) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleCopy = useCallback(async () => {
    if (!copyText) return;

    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        setCopied(false);
        timerRef.current = null;
      }, 2000);
    } catch {
      setCopied(false);
    }
  }, [copyText]);

  return (
    <div className={cn("overflow-hidden rounded-xl border border-white/[0.08]", className)}>
      <div className="flex items-center justify-between border-b border-white/[0.06] bg-white/[0.04] px-4 py-2.5">
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
            <div className="h-2.5 w-2.5 rounded-full bg-yellow-500/70" />
            <div className="h-2.5 w-2.5 rounded-full bg-green-500/70" />
          </div>
          <span className="ml-2 text-xs text-muted-foreground/70">{title ?? "Terminal"}</span>
        </div>
        {copyText || headerAction ? (
          <div className="flex items-center gap-2 shrink-0">
            {copyText ? (
              <button
                type="button"
                aria-label={copied ? "Copied command" : "Copy command"}
                className={cn(
                  "text-muted-foreground/50 transition-colors hover:text-muted-foreground",
                  copied && "text-emerald-400 hover:text-emerald-400"
                )}
                onClick={() => {
                  void handleCopy();
                }}
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
            ) : null}
            {headerAction}
          </div>
        ) : null}
      </div>
      <div className="bg-[#0D0D0F] p-4 font-mono text-sm leading-relaxed text-foreground/80">{children}</div>
    </div>
  );
}
